import { useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';

import { pauseIconPixelDot, playIconPixelDot } from './pixel-dot-play-pause-icons';

export interface PlayPauseControlsProps {
	isPlaying: boolean;
	onToggle: () => void;
	/** Accessible label for the controlled media (e.g. "Product demo animation").
	 *  Used to build descriptive aria-labels: "Play Product demo animation" / "Pause Product demo animation" */
	mediaLabel?: string;
}

export const PlayPauseControls = ({ isPlaying, onToggle, mediaLabel }: PlayPauseControlsProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isDarkTheme } = useDarkTheme();

	const handleToggleClick = useCallback(() => {
		const intent: 'play' | 'pause' = isPlaying ? 'pause' : 'play';
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'playPauseControlButton',
			eventType: 'ui',
			eventComponent: 'button',
			source: 'play-pause-controls',
			attributes: {
				intent,
			},
		}).fire();
		onToggle();
	}, [createAnalyticsEvent, isPlaying, onToggle]);

	const playLabel = mediaLabel
		? intl.formatMessage(
				{
					id: 'play-pause-controls.play-with-label',
					defaultMessage: 'Play {mediaLabel}',
				},
				{ mediaLabel },
			)
		: intl.formatMessage({
				id: 'play-pause-controls.play',
				defaultMessage: 'Play',
			});

	const pauseLabel = mediaLabel
		? intl.formatMessage(
				{
					id: 'play-pause-controls.pause-with-label',
					defaultMessage: 'Pause {mediaLabel}',
				},
				{ mediaLabel },
			)
		: intl.formatMessage({
				id: 'play-pause-controls.pause',
				defaultMessage: 'Pause',
			});

	return (
		<button
			type="button"
			onClick={handleToggleClick}
			aria-label={isPlaying ? pauseLabel : playLabel}
			aria-pressed={isPlaying}
			data-testid="play-pause-controls"
			css={[controlButtonCss, isDarkTheme && darkThemeControlButtonCss]}
		>
			{isDarkTheme ? (
				isPlaying ? (
					pauseIconPixelDot
				) : (
					playIconPixelDot
				)
			) : (
				<div css={buttonCircleCss}>
					<span css={iconGlyphCss} aria-hidden={true}>
						{isPlaying ? (
							<VideoPauseOverlayIcon label="" size="medium" color="currentColor" />
						) : (
							<VideoPlayOverlayIcon label="" size="medium" color="currentColor" />
						)}
					</span>
				</div>
			)}
		</button>
	);
};

const controlButtonCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	flex: '0 0 auto',
	aspectRatio: '1 / 1',
	padding: token('space.0'),
	backgroundColor: 'transparent',
	border: 'none',
	borderRadius: '100%',
	cursor: 'pointer',
	blockSize: token('space.500'),
	inlineSize: token('space.500'),
	minBlockSize: token('space.500'),
	minInlineSize: token('space.500'),
	maxBlockSize: token('space.500'),
	maxInlineSize: token('space.500'),
	// Focus ring for keyboard accessibility
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
	'--button-color': wacToken('color.neutral.light300'),
	'--hover-color': wacToken('color.neutral.light400'),
	'--active-color': wacToken('color.neutral.light400'),
});

const darkThemeControlButtonCss = css({
	'--button-color': wacToken('color.neutral.light0'),
	'--hover-color': wacToken('color.neutral.light800'),
	'--active-color': wacToken('color.neutral.light800'),
	color: 'var(--button-color)',
	'&:hover': {
		color: 'var(--hover-color)',
	},
	'&:active': {
		color: 'var(--active-color)',
	},
});

const buttonCircleCss = css({
	backgroundColor: 'var(--button-color)',
	borderRadius: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	boxSizing: 'border-box',
	flex: '0 0 auto',
	aspectRatio: '1 / 1',
	blockSize: token('space.500'),
	inlineSize: token('space.500'),
	minBlockSize: token('space.500'),
	minInlineSize: token('space.500'),
	maxBlockSize: token('space.500'),
	maxInlineSize: token('space.500'),
	// Tablet & below: shrink the visible circle to 32px while keeping the
	// 40x40 button as the tap target (per GWP-11496).
	[media.below.lg]: {
		blockSize: token('space.400'),
		inlineSize: token('space.400'),
		minBlockSize: token('space.400'),
		minInlineSize: token('space.400'),
		maxBlockSize: token('space.400'),
		maxInlineSize: token('space.400'),
	},
	'&:hover': {
		backgroundColor: 'var(--hover-color)',
	},
	'&:active': {
		backgroundColor: 'var(--active-color)',
	},
});

/** Figma: black glyphs on the neutral circular background */
const iconGlyphCss = css({
	color: wacToken('color.primary.black'),
	display: 'flex',
	lineHeight: 0,
});
